/**
 * symbols.jsx
 * ---------------------------------------------------------------------------
 * Reusable hand-drawn SVG primitives composed into the fifteen named
 * CoConsoleX diagrams. Strict pen-on-paper aesthetic: 1.5–2px round strokes,
 * no fills, no shadows, no gradients.
 */

import React from "react";
import { cxTokens as t } from "../../lib/coconsolexTokens.js";

const baseLine = {
  fill: "none",
  strokeLinecap: "round",
  strokeLinejoin: "round",
};

export const LawyerSilhouette = ({ x = 0, y = 0, color = t.ink }) => (
  <g transform={`translate(${x}, ${y})`} stroke={color} strokeWidth="1.6" {...baseLine}>
    <circle cx="0" cy="-22" r="6" />
    <path d="M -10 -10 Q 0 -16 10 -10 L 10 14 L -10 14 Z" fill="none" />
    <path d="M 10 0 L 18 6 L 18 18 L 10 18" />
    <path d="M 14 6 L 14 18" />
  </g>
);

export const ServerIcon = ({ x = 0, y = 0, color = t.ink }) => (
  <g transform={`translate(${x}, ${y})`} stroke={color} strokeWidth="1.6" {...baseLine}>
    <rect x="-22" y="-18" width="44" height="36" />
    <line x1="-22" y1="-6" x2="22" y2="-6" />
    <line x1="-22" y1="6" x2="22" y2="6" />
    <circle cx="-16" cy="-12" r="1.4" fill={color} />
    <circle cx="-16" cy="0" r="1.4" fill={color} />
    <circle cx="-16" cy="12" r="1.4" fill={color} />
  </g>
);

export const VaultIcon = ({ x = 0, y = 0, color = t.ink }) => (
  <g transform={`translate(${x}, ${y})`} stroke={color} strokeWidth="1.6" {...baseLine}>
    <rect x="-30" y="-26" width="60" height="52" />
    <circle cx="0" cy="0" r="14" />
    <line x1="-14" y1="-14" x2="-22" y2="-22" />
    <line x1="14" y1="-14" x2="22" y2="-22" />
    <line x1="-14" y1="14" x2="-22" y2="22" />
    <line x1="14" y1="14" x2="22" y2="22" />
  </g>
);

export const CloudIcon = ({ x = 0, y = 0, label, faded = false }) => {
  const stroke = faded ? t.rule : t.slate;
  return (
    <g transform={`translate(${x}, ${y})`}>
      <path
        d="M -22 4 Q -28 -8 -14 -10 Q -10 -20 4 -16 Q 18 -22 22 -8 Q 30 -4 22 6 L -22 6 Z"
        fill="none"
        stroke={stroke}
        strokeWidth={faded ? "1.2" : "1.5"}
        strokeDasharray={faded ? "3 3" : ""}
        {...baseLine}
      />
      {label && (
        <text
          x="0"
          y="22"
          textAnchor="middle"
          fontFamily="Inter, sans-serif"
          fontSize="10"
          fill={stroke}
          letterSpacing="0.06em"
        >
          {label}
        </text>
      )}
    </g>
  );
};

export const DiodeArrow = ({ x1, y1, x2, y2, color = t.diodeGreen }) => (
  <g stroke={color} strokeWidth="1.8" {...baseLine}>
    <line x1={x1} y1={y1} x2={x2} y2={y2} />
    <polygon
      points={`${x2},${y2} ${x2 - 8},${y2 - 5} ${x2 - 8},${y2 + 5}`}
      fill={color}
    />
    <circle cx={(x1 + x2) / 2} cy={(y1 + y2) / 2} r="6" fill="none" />
    <line
      x1={(x1 + x2) / 2 - 4}
      y1={(y1 + y2) / 2}
      x2={(x1 + x2) / 2 + 4}
      y2={(y1 + y2) / 2}
    />
  </g>
);

export const HairlineRing = ({ cx, cy, r, color = t.ink, dash = "" }) => (
  <circle
    cx={cx}
    cy={cy}
    r={r}
    fill="none"
    stroke={color}
    strokeWidth="1.4"
    strokeDasharray={dash}
  />
);

export const CautionFlag = ({ x = 0, y = 0 }) => (
  <g transform={`translate(${x}, ${y})`} stroke={t.cautionAmber} strokeWidth="1.5" {...baseLine}>
    <polygon points="0,-12 11,8 -11,8" fill="none" />
    <line x1="0" y1="-4" x2="0" y2="2" />
    <circle cx="0" cy="5" r="0.8" fill={t.cautionAmber} />
  </g>
);

/** Wrapper for every diagram so the figure + caption render consistently. */
export const Figure = ({ children, caption, ariaLabel, vb = "0 0 600 360" }) => (
  <figure
    style={{
      margin: "0 0 32px 0",
      padding: "24px",
      background: t.cream,
      border: `1px solid ${t.rule}`,
    }}
  >
    <svg
      viewBox={vb}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label={ariaLabel}
      style={{ width: "100%", height: "auto", display: "block" }}
    >
      {children}
    </svg>
    {caption && (
      <figcaption
        style={{
          fontFamily: "'Cormorant Garamond', serif",
          fontStyle: "italic",
          fontSize: 15,
          color: t.slate,
          textAlign: "center",
          marginTop: 16,
          letterSpacing: "0.01em",
        }}
      >
        {caption}
      </figcaption>
    )}
  </figure>
);
